/** Tells the other tabs of this browser when the signed-in account changes or signs out. */

const AUTH_CHANGE_CHANNEL = "pax.auth-changes";

type AuthChange = { type: "account-changed"; userId: string } | { type: "signed-out" };

let authChangeChannel: BroadcastChannel | null | undefined;

function getAuthChangeChannel(): BroadcastChannel | null {
  if (authChangeChannel !== undefined) return authChangeChannel;

  try {
    authChangeChannel =
      typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(AUTH_CHANGE_CHANNEL);
  } catch {
    authChangeChannel = null;
  }
  return authChangeChannel;
}

function broadcastAuthChange(change: AuthChange): void {
  getAuthChangeChannel()?.postMessage(change);
}

export function broadcastSignOut(): void {
  broadcastAuthChange({ type: "signed-out" });
}

export function broadcastAccountChange(userId: string): void {
  broadcastAuthChange({ type: "account-changed", userId });
}

export function subscribeToAuthChanges(listener: (change: AuthChange) => void): () => void {
  const channel = getAuthChangeChannel();
  if (!channel) return () => undefined;

  function handleMessage(event: MessageEvent<unknown>): void {
    const value = event.data;
    if (!value || typeof value !== "object") return;

    const change = value as Partial<AuthChange>;
    if (change.type === "signed-out") {
      listener({ type: "signed-out" });
    } else if (change.type === "account-changed" && typeof change.userId === "string") {
      listener({ type: "account-changed", userId: change.userId });
    }
  }

  channel.addEventListener("message", handleMessage);
  return () => channel.removeEventListener("message", handleMessage);
}
